import React from "react";
import { Box, Grid, Typography } from "@mui/material";

import Header from "../../components/Header";
import DashboardCard from "../../components/DashboardCard";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import StatusChip from "../../components/StatusChip";

import useOperationalClarityDashboard from "../../hooks/useOperationalClarityDashboard";
import { useTenant } from "../../context/TenantContext";

import { formatEnum } from "../../utils/formatEnum";

import {
  riskTone,
  impactTone,
  priorityTone,
  countRiskTone,
  countDangerTone,
} from "../../status/StatusChipTones";

const OperationalClarityDashboard = () => {
  const { tenantId } = useTenant();

  const { data, loading, error } = useOperationalClarityDashboard({
    tenantId,
  });

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CURRENT BUSINESS RISK"
          subtitle="Select an audit to view operational baseline insights"
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Header
          title="CURRENT BUSINESS RISK"
          subtitle="Loading current business risk dashboard..."
        />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Header
          title="CURRENT BUSINESS RISK"
          subtitle="Failed to load current business risk dashboard"
        />
      </Box>
    );
  }

  const areaColumns = [
    {
      field: "area",
      headerName: "Area",
      flex: 1.2,
      minWidth: 160,
      valueFormatter: (value) => formatEnum(value),
    },
    {
      field: "totalActivities",
      headerName: "Activities",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "processesIdentified",
      headerName: "Identified",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "documentedProcesses",
      headerName: "Documented",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "unownedProcesses",
      headerName: "Unowned",
      flex: 0.8,
      minWidth: 110,
      renderCell: ({ value }) => (
        <StatusChip
          label={value}
          tone={countRiskTone(value)}
        />
      ),
    },
    {
      field: "singlePersonDependencies",
      headerName: "One Person",
      flex: 0.9,
      minWidth: 130,
      renderCell: ({ value }) => (
        <StatusChip
          label={value}
          tone={countDangerTone(value)}
        />
      ),
    },
    {
      field: "averageRiskScore",
      headerName: "Avg Risk",
      flex: 0.8,
      minWidth: 110,
      renderCell: ({ value }) => (
        <RiskChip value={value} />
      ),
    },
  ];

  const riskColumns = [
    {
      field: "statement",
      headerName: "Process",
      flex: 1.3,
      minWidth: 200,
    },
    {
      field: "area",
      headerName: "Area",
      flex: 0.9,
      minWidth: 140,
      valueFormatter: (value) => formatEnum(value),
    },
    {
      field: "businessImpact",
      headerName: "Impact",
      flex: 0.8,
      minWidth: 110,
      renderCell: ({ value }) => (
        <ImpactChip value={value} />
      ),
    },
    {
      field: "healthScore",
      headerName: "Health",
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ value }) => (
        <ScoreText value={value} suffix="%" />
      ),
    },
    {
      field: "riskScore",
      headerName: "Risk",
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ value }) => (
        <RiskChip value={value} />
      ),
    },
    {
      field: "reasons",
      headerName: "Reasons",
      flex: 1.5,
      minWidth: 220,
      renderCell: ({ value }) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {(value || []).slice(0, 3).map((reason) => (
            <StatusChip
              key={reason}
              label={reason}
              tone="default"
            />
          ))}
        </Box>
      ),
    },
  ];

  const actionColumns = [
    {
      field: "statement",
      headerName: "Process",
      flex: 1.3,
      minWidth: 200,
    },
    {
      field: "area",
      headerName: "Area",
      flex: 0.8,
      minWidth: 130,
      valueFormatter: (value) => formatEnum(value),
    },
    {
      field: "actionType",
      headerName: "Action",
      flex: 1,
      minWidth: 170,
      renderCell: ({ value }) => (
        <StatusChip
          label={formatEnum(value)}
          tone="teal"
        />
      ),
    },
    {
      field: "priority",
      headerName: "Priority",
      flex: 0.7,
      minWidth: 110,
      renderCell: ({ value }) => (
        <PriorityChip value={value} />
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1.6,
      minWidth: 240,
    },
  ];

  const summary = data.summary;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Header
          title="CURRENT BUSINESS RISK"
          subtitle="Baseline, risk profile and recommended next actions to answer: where are we vulnerable today?"
        />
      </Box>

      <Grid container spacing={2}>
        <DashboardCard
          cardTitle="Activities Defined"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue value={summary.totalActivities} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Documented Processes"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue value={summary.documentedProcesses} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Single-Person Dependencies"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue
            value={summary.singlePersonDependencies}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Clarity Score"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue
            value={`${Math.round(
              summary.operationalResilienceScore || 0,
            )}%`}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Unowned Processes"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue value={summary.unownedProcesses} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Training Gaps"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue value={summary.staffTrainingGaps} />
        </DashboardCard>

        <DashboardCard
          cardTitle="High-Impact Gaps"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue value={summary.highImpactGaps} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Average Risk"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue
            value={`${Math.round(
              summary.averageRiskScore || 0,
            )}%`}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Recommended Next Actions"
          xs={12}
          md={12}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.recommendedActions || []}
            columns={actionColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Highest Risk Activities"
          xs={12}
          md={12}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.highestRiskActivities || []}
            columns={riskColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Area Breakdown"
          xs={12}
          md={12}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.areaRows || []}
            columns={areaColumns}
            getRowId={(row) => row.area}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>
      </Grid>
    </Box>
  );
};

const StatValue = ({ value }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="100%"
  >
    <Typography variant="h2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const ScoreText = ({ value, suffix = "" }) => (
  <Typography variant="body2" fontWeight={600}>
    {Math.round(value || 0)}
    {suffix}
  </Typography>
);

const RiskChip = ({ value }) => {
  const score = Math.round(value || 0);

  return (
    <StatusChip
      label={score}
      tone={riskTone(score)}
    />
  );
};

const ImpactChip = ({ value }) => (
  <StatusChip
    label={value ? formatEnum(value) : "Unknown"}
    tone={impactTone(value)}
  />
);

const PriorityChip = ({ value }) => (
  <StatusChip
    label={value ? formatEnum(value) : "Unknown"}
    tone={priorityTone(value)}
  />
);

export default OperationalClarityDashboard;